/**
 * Shared runtime guard for the chrome `Content` wrappers (COM-182 / COM-189
 * wave 4C): warn-log once per model+name when a live entry references a block
 * the surface's registry cannot resolve — a missing name renders BLANK, so
 * this makes a future chrome content edit diagnosable in one log line.
 *
 * Imports only the builtin-name set (no component definitions), so it is safe
 * to reach from the synchronous visible-chrome surfaces.
 */
import { collectRenderedBlockNames } from "./block-names";
import { BUILDER_BUILTIN_BLOCK_NAMES } from "./visible-chrome-registry";

export function makeChromeBlockGuard(
  registeredNames: ReadonlySet<string>,
  registryHint: string,
): (content: unknown, model: string | undefined) => void {
  /**
   * Guard memo: entries are stable objects (loader data), so each is walked
   * once per isolate/session, not on every re-render.
   */
  const checkedEntries = new WeakSet<object>();
  /** One warning per model+name per isolate/session — greppable, not spammy. */
  const warnedBlockNames = new Set<string>();

  return (content, model) => {
    if (!content || typeof content !== "object") return;
    if (checkedEntries.has(content)) return;
    checkedEntries.add(content);

    for (const name of collectRenderedBlockNames(content)) {
      if (registeredNames.has(name)) continue;
      if (BUILDER_BUILTIN_BLOCK_NAMES.has(name)) continue;
      const key = `${model ?? "?"}:${name}`;
      if (warnedBlockNames.has(key)) continue;
      warnedBlockNames.add(key);
      console.warn(
        `[chrome-registry] Builder "${model ?? "?"}" entry references block "${name}" ` +
          `that is not in ${registryHint} — it will render BLANK. If it is a ` +
          `registered page block, add its definition there and re-run ` +
          `scripts/audit-chrome-blocks.mjs; if it is not registered anywhere, ` +
          `fix the entry in Builder.`,
      );
    }
  };
}
